import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import { SpacePerformanceReportSummary } from '../../api/Rest/space';
import {
  FolderBreadcrumbText,
  LoadingSpinner,
  StationCellProps,
  StationCellType,
} from '../../components/Molecules';
import {
  AboutDailyPracticePopup,
  ClubAccessRestrictionPopup,
  ClubSelectFolderPopup,
  GameTypeHelpPopup,
  GameTypeHelpType,
  SelectPracticePeriodPopup,
  stringToGameTypeHelpType,
} from '../../components/Popups';
import {
  AIMemoryBoostVer2CellColour,
  AIMemoryBoostVer2CellProps,
  AIMemoryBoostVer2Template,
} from '../../components/Templates';
import { AIRevisionStatus } from '../../constants/MemoryGames/aiRevision';
import { AppToastInfoOption } from '../../constants/toast';
import { usePopup } from '../../context';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { GameRecord, RevisionSchedule } from '../../models/RecordStore';
import { GameListsType } from '../../models/SessionStore';
import { PerformanceGame } from '../../models/SpaceStore';
import { WebBoosterType } from '../../models/UserStore';
import {
  useRecordStore,
  useRecordStoreListField,
} from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getTimeString } from '../../utils/date';
import {
  getPeriod,
  getPracticePeriod,
  PeriodType,
  PracticePeriodType,
} from '../../utils/period';

const { fetchSpace } = useSpaceStore.getState();
const { fetchRevisionSchedules } = useRecordStore.getState();

type State = {
  selectedPeriod: PracticePeriodType;
  from: Date | undefined;
  to: Date | undefined;
  selectedBoosterType: WebBoosterType | undefined;
  partitionId: string;
  loadingTotal: number;
  finishInitSetting: boolean; // for check the revision schedule turn to the ids need to fetch
  readyGameLinks: string[];
  isFinishedFetchSpace: boolean;
  performanceData: SpacePerformanceReportSummary | undefined;
};

const { getPerformanceReport } = useSpaceStore.getState();

const AIMemoryBoostScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { classroomId, gameSetId } = useParams();
  const { showPopup, hidePopup } = usePopup();

  const userId = useSessionStore((state) => state.activeUserId);
  const partitionId = useSessionStore((store) => store.partitionId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));
  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));
  const webBoosterType = userAccount?.settings.webBoosterType;

  const currentDate = useMemo(() => new Date(), []);
  const isWebPlanUnlocked = !!userAccount?.activeWebPlanTier;

  const todayPeriod = useMemo(() => {
    return getPeriod(currentDate, PeriodType.Today, i18n.language);
  }, [currentDate, i18n.language]);

  const [state, setState] = useState<State>({
    selectedPeriod: PracticePeriodType.Overall,
    from: undefined,
    to: undefined,
    selectedBoosterType: userAccount?.settings.webBoosterType,
    partitionId: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.All}`,
    loadingTotal: 0,
    finishInitSetting: false,
    readyGameLinks: [],
    isFinishedFetchSpace: false,
    performanceData: undefined,
  });

  useEffect(() => {
    setState((s) => ({
      ...s,
      partitionId:
        partitionId ??
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`,
    }));
  }, [partitionId, state.selectedBoosterType]);

  useRedirectEntry();

  // MARK: Get Space
  const spaceFieldName = `${classroomId}`;
  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId, spaceFieldName]);

  const classroom = useSpaceStore(
    (store) => store.getSpace(classroomId ?? ''),
    shallow,
  );

  useRedirectHome(state.isFinishedFetchSpace && !classroom);

  const breadcrumbs = useMemo(() => {
    const folders =
      classroom?.getAllPartitions(userAccount?.settings.webBoosterType) ?? [];
    const selectFolder = folders.find(
      (folder) => folder.partitionId === state.partitionId,
    );
    if (selectFolder) {
      return [selectFolder];
    }
    return [];
  }, [userAccount?.settings.webBoosterType, classroom, state.partitionId]);

  const readyFieldName = `${AIRevisionStatus.Ready}_${userId}_${classroomId}_${gameSetId}`;
  const scheduledFieldName = `${AIRevisionStatus.Scheduled}_${userId}_${classroomId}_${gameSetId}`;
  const completedFieldName = `${AIRevisionStatus.Completed}_${userId}_${classroomId}_${gameSetId}`;
  useRecordStoreListField('revisionSchedules', readyFieldName);
  useRecordStoreListField('revisionSchedules', scheduledFieldName);
  useRecordStoreListField('revisionSchedules', completedFieldName);
  useSpaceStoreListField('performanceGames', readyFieldName);
  useSpaceStoreListField('performanceGames', scheduledFieldName);
  useSpaceStoreListField('performanceGames', completedFieldName);

  useEffect(() => {
    if (classroomId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchRevisionSchedules({
        classroomId,
        gameSetId,
        userId: userId ?? '',
        webBoosterType: webBoosterType,
        partitionId:
          partitionId === `${webBoosterType}-${FolderBreadcrumbText.All}`
            ? undefined
            : partitionId ?? undefined,
        from: state.from?.getTime().toString(),
        to: state.to?.getTime().toString(),
      })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [
    classroomId,
    gameSetId,
    partitionId,
    state.from,
    state.to,
    userId,
    webBoosterType,
  ]);

  const {
    readyRevisionSchedules,
    scheduledRevisionSchedules,
    completedRevisionSchedules,
  } = useRecordStore((store) => {
    const _readyRevisionSchedules = store.getRevisionSchedules(readyFieldName);
    const _scheduledRevisionSchedules =
      store.getRevisionSchedules(scheduledFieldName);
    const _completedRevisionSchedules =
      store.getRevisionSchedules(completedFieldName);
    return {
      readyRevisionSchedules: _readyRevisionSchedules,
      scheduledRevisionSchedules: _scheduledRevisionSchedules,
      completedRevisionSchedules: _completedRevisionSchedules,
    };
  }, arrayEqual(shallow));

  const { readyRevisionGames, scheduledRevisionGames, completedRevisionGames } =
    useSpaceStore((store) => {
      const _readyRevisionGames =
        store.getPerformanceReportGames(readyFieldName);
      const _scheduledRevisionGames =
        store.getPerformanceReportGames(scheduledFieldName);
      const _completedRevisionGames =
        store.getPerformanceReportGames(completedFieldName);
      return {
        readyRevisionGames: _readyRevisionGames,
        scheduledRevisionGames: _scheduledRevisionGames,
        completedRevisionGames: _completedRevisionGames,
      };
    }, arrayEqual(shallow));

  const onClickGameInfo = useCallback(
    (type: GameTypeHelpType) => {
      showPopup(<GameTypeHelpPopup type={type} onPrimary={hidePopup} />);
    },
    [hidePopup, showPopup],
  );

  const onClubAccessRestricted = useCallback(() => {
    showPopup(
      <ClubAccessRestrictionPopup
        onDetails={() => {
          hidePopup();
          navigate('/subscription');
        }}
        onCancel={hidePopup}
      />,
    );
  }, [showPopup, hidePopup, navigate]);

  const convertRevisionGamesToStations = useCallback(
    (revisionGames: PerformanceGame[], aIRevisionStatus: AIRevisionStatus) => {
      if (revisionGames && userProfile) {
        const gameLinkList = revisionGames.map(
          (revisionGame) =>
            `/club/${revisionGame.gameRecord.classroomId}/journey/${revisionGame.gameRecord.gameSetId}/game/${revisionGame.gameRecord.gameId}?mode=revision`,
        );

        const stationCells: StationCellProps[] = revisionGames.map(
          (revisionGame) => {
            const gameItems = revisionGame.gameCollection.getPartItemArray(
              (item) => item.game,
            );
            const gameItem = gameItems.find(
              (item) => item.game?.gameId === revisionGame.game.gameId,
            );

            return {
              type: StationCellType.Game,
              props: {
                game: revisionGame.game,
                stationId: undefined, // stationStyle?.stations.game ?? StationGameItemNo.s1,
                name: '',
                index: -1, // stationIndex,
                iconKey: gameItem?.icon?.tag ?? '',
                playCount:
                  aIRevisionStatus === AIRevisionStatus.Ready
                    ? undefined
                    : aIRevisionStatus === AIRevisionStatus.Scheduled
                    ? undefined
                    : revisionGame.gameStatistics.playCount ?? 0,
                averageScore:
                  aIRevisionStatus === AIRevisionStatus.Ready
                    ? undefined
                    : aIRevisionStatus === AIRevisionStatus.Scheduled
                    ? undefined
                    : revisionGame.gameStatistics.averageScore ?? 0,
                userProfile: userProfile,
                remainingBreadReward: 0,
                recordScore: revisionGame.gameRecord.score,
                recordDuration: revisionGame.gameRecord.durationInMs
                  ? getTimeString(
                      revisionGame.gameRecord.durationInMs,
                      'HH:mm:ss.SS',
                      true,
                    )
                  : undefined,
                isUnlocked: true,
                aiRevisionStatus: aIRevisionStatus,
                onClickPlay: () => {
                  if (!isWebPlanUnlocked) {
                    onClubAccessRestricted();
                  } else {
                    if (aIRevisionStatus !== AIRevisionStatus.Scheduled) {
                      if (aIRevisionStatus === AIRevisionStatus.Ready) {
                        const gameLinks = gameLinkList.filter(
                          (link) =>
                            link !==
                            `/club/${revisionGame.gameRecord.classroomId}/journey/${revisionGame.gameRecord.gameSetId}/game/${revisionGame.gameRecord.gameId}?mode=revision`,
                        );
                        useSessionStore.getState().setGameListsData({
                          type: GameListsType.Revision,
                          gameLinkList: gameLinks,
                          totalGames: gameLinks.length,
                        });
                      }
                      navigate(
                        `/club/${revisionGame.gameRecord.classroomId}/journey/${revisionGame.gameRecord.gameSetId}/game/${revisionGame.gameRecord.gameId}?mode=revision`,
                      );
                    } else {
                      toast(
                        t("The scheduled revision isn't avaliable yet."),
                        AppToastInfoOption,
                      );
                    }
                  }
                },
                onClickAnalysis: () => {
                  navigate(
                    `/club/${revisionGame.gameRecord.classroomId}/journey/${revisionGame.gameRecord.gameSetId}/game/${revisionGame.gameRecord.gameId}/analysis`,
                  );
                },
                onClickInfo: () => {
                  const popupInfoType = stringToGameTypeHelpType(
                    gameItem?.icon?.tag,
                  );
                  if (popupInfoType) {
                    onClickGameInfo(popupInfoType);
                  }
                },
              },
            };
          },
        );

        return stationCells;
      }
      return [];
    },
    [
      isWebPlanUnlocked,
      navigate,
      onClickGameInfo,
      onClubAccessRestricted,
      t,
      userProfile,
    ],
  );

  const readyStatusItem: StationCellProps[] = useMemo(() => {
    const revisionSchedule = convertRevisionGamesToStations(
      readyRevisionGames,
      AIRevisionStatus.Ready,
    );
    return revisionSchedule;
  }, [convertRevisionGamesToStations, readyRevisionGames]);

  const scheduledStatusItem = useMemo(() => {
    const revisionSchedule = convertRevisionGamesToStations(
      scheduledRevisionGames,
      AIRevisionStatus.Scheduled,
    );
    return revisionSchedule;
  }, [convertRevisionGamesToStations, scheduledRevisionGames]);

  const completedStatusItem = useMemo(() => {
    const revisionSchedule = convertRevisionGamesToStations(
      completedRevisionGames,
      AIRevisionStatus.Completed,
    );
    return revisionSchedule;
  }, [completedRevisionGames, convertRevisionGamesToStations]);

  // Select the element
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const elements = document.querySelectorAll(
    '.game-station-cell .title',
  ) as NodeListOf<HTMLElement>;

  elements.forEach((element) => {
    element.style.cssText = `
      -webkit-line-clamp: 1;
    `;
  });

  const getBgColour = useCallback(
    (index: number): AIMemoryBoostVer2CellColour => {
      const remainder = index % 8;
      switch (remainder) {
        case 0:
          return AIMemoryBoostVer2CellColour.red;
        case 1:
          return AIMemoryBoostVer2CellColour.orange;
        case 2:
          return AIMemoryBoostVer2CellColour.yellow;
        case 3:
          return AIMemoryBoostVer2CellColour.green;
        case 4:
          return AIMemoryBoostVer2CellColour.blue;
        case 5:
          return AIMemoryBoostVer2CellColour.navyBlue;
        case 6:
          return AIMemoryBoostVer2CellColour.purple;
        case 7:
          return AIMemoryBoostVer2CellColour.turquoise;
        default:
          return AIMemoryBoostVer2CellColour.red;
      }
    },
    [],
  );

  const getDateRelated = useCallback(
    (revisionGame: PerformanceGame, revisionSchedule: RevisionSchedule) => {
      const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      };

      const formattedDate = new Intl.DateTimeFormat(i18n.language, options)
        .format(revisionSchedule.willReviseAt)
        .replace(',', '');

      const lastRecord =
        revisionSchedule.records[revisionSchedule.records.length - 1];
      const daysSinceStart = lastRecord
        ? (() => {
            switch (lastRecord.nextPhase) {
              case 1:
                return t('{n} minutes from playing', { n: '10' });
              case 2:
                return t('{n} hours from playing', { n: '0.5' });
              case 3:
                return t('{n} hours from playing', { n: '3' });
              case 4:
                return t('{n} hours from playing', { n: '6' });
              case 5:
                return t('{n} days from playing', { n: '1' });
              case 6:
                return t('{n} days from playing', { n: '3' });
              case 7:
                return t('{n} days from playing', { n: '7' });
              case 8:
                return t('{n} days from playing', { n: '15' });
              case 9:
                return t('{n} days from playing', { n: '30' });
              case 10:
                return t('{n} days from playing', { n: '60' });
              case 11:
                return t('{n} days from playing', { n: '180' });
              case 12:
                return t('{n} year from playing', { n: '1' });
              default:
                return t('played just now');
            }
          })()
        : t('played just now');

      const score = revisionGame.gameRecord.score ?? 0;

      return { date: formattedDate, days: daysSinceStart, score: score };
    },
    [i18n.language, t],
  );

  const getRecentGamesAverageScore = useCallback(
    (revisionSchedule: RevisionSchedule, gameRecord: GameRecord) => {
      // TODO: seems that the percentage not quite accurate
      const recentRecords = revisionSchedule.forgettingCurve.slice(-3);
      const averageScore =
        recentRecords.length > 0
          ? recentRecords.reduce(
              (acc, cur) => acc + cur.percentage,
              gameRecord.score ?? 0,
            ) /
            (recentRecords.length + 1)
          : 0;
      return numbro(averageScore).format({
        mantissa: 2,
        trimMantissa: true,
        optionalMantissa: true,
      });
    },
    [],
  );

  const isNeedReviseAgain = useCallback((revisionGame: PerformanceGame) => {
    const dateOfRecord = revisionGame.gameRecord.startedAt;
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return (
      (revisionGame.gameRecord.score ?? 0) < 90 && dateOfRecord >= sevenDaysAgo
    );
  }, []);

  const convertRevisionStationsToCells = useCallback(
    (
      revisionStations: StationCellProps[],
      revisionSchedules: RevisionSchedule[],
      revisionGames: PerformanceGame[],
      aIRevisionStatus: AIRevisionStatus,
      { isWhiteBackground }: { isWhiteBackground: boolean } = {
        isWhiteBackground: true,
      },
    ) => {
      return revisionStations
        .map((station, index) => {
          if (station.type === StationCellType.Game) {
            const gameId = station.props.game.gameId;
            const revisionGame = revisionGames.find(
              (game) => game.game.gameId === gameId,
            );
            const revisionSchedule = revisionSchedules.find(
              (schedule) => schedule.gameId === gameId,
            );
            if (revisionGame && revisionSchedule) {
              const dateRelated = getDateRelated(
                revisionGame,
                revisionSchedule,
              );
              const avgScore = getRecentGamesAverageScore(
                revisionSchedule,
                revisionGame.gameRecord,
              );

              return {
                stationCell: cloneDeep(station),
                priority:
                  aIRevisionStatus === AIRevisionStatus.Ready
                    ? index + 1
                    : undefined,
                recentPlayedDate: dateRelated.date || '',
                daysFromPlaying: dateRelated.days,
                recent3GamesAverage: avgScore,
                backgroundColour: isWhiteBackground
                  ? AIMemoryBoostVer2CellColour.white
                  : getBgColour(index),
                isReviseLater: aIRevisionStatus === AIRevisionStatus.Scheduled,
              };
            }
          }
          return undefined;
        })
        .filter(Boolean) as AIMemoryBoostVer2CellProps[];
    },
    [getBgColour, getDateRelated, getRecentGamesAverageScore],
  );

  const needToReviseNowItems = useMemo<AIMemoryBoostVer2CellProps[]>(() => {
    return convertRevisionStationsToCells(
      readyStatusItem,
      readyRevisionSchedules,
      readyRevisionGames,
      AIRevisionStatus.Ready,
      { isWhiteBackground: false },
    );
  }, [
    convertRevisionStationsToCells,
    readyRevisionGames,
    readyRevisionSchedules,
    readyStatusItem,
  ]);

  const toBeRevisedLaterItems = useMemo<AIMemoryBoostVer2CellProps[]>(() => {
    return convertRevisionStationsToCells(
      scheduledStatusItem,
      scheduledRevisionSchedules,
      scheduledRevisionGames,
      AIRevisionStatus.Scheduled,
    );
  }, [
    convertRevisionStationsToCells,
    scheduledRevisionGames,
    scheduledRevisionSchedules,
    scheduledStatusItem,
  ]);

  const recentlyRevisedItems = useMemo<AIMemoryBoostVer2CellProps[]>(() => {
    return convertRevisionStationsToCells(
      completedStatusItem,
      completedRevisionSchedules,
      completedRevisionGames,
      AIRevisionStatus.Scheduled,
    );
  }, [
    convertRevisionStationsToCells,
    completedStatusItem,
    completedRevisionSchedules,
    completedRevisionGames,
  ]);

  const needToReviseAgainItems = useMemo<AIMemoryBoostVer2CellProps[]>(() => {
    const needToReviseItem = readyStatusItem.filter((item) => {
      if (item.type === StationCellType.Game) {
        const gameId = item.props.game.gameId;
        const revisionGame = readyRevisionGames.find(
          (game) => game.game.gameId === gameId,
        );
        if (revisionGame && isNeedReviseAgain(revisionGame)) {
          return true;
        }
      }
      return false;
    });
    return convertRevisionStationsToCells(
      needToReviseItem.slice(0, 30),
      readyRevisionSchedules,
      readyRevisionGames,
      AIRevisionStatus.Ready,
    );
  }, [
    convertRevisionStationsToCells,
    isNeedReviseAgain,
    readyRevisionGames,
    readyRevisionSchedules,
    readyStatusItem,
  ]);

  const onClickBoostGame = useCallback(() => {
    const gameLinkList = readyRevisionGames.map(
      (revisionGame) =>
        `/club/${revisionGame.gameRecord.classroomId}/journey/${revisionGame.gameRecord.gameSetId}/game/${revisionGame.gameRecord.gameId}?mode=revision`,
    );

    const firstGame = gameLinkList[0];
    if (firstGame) {
      const newGameLinkList = gameLinkList.slice(1);
      useSessionStore.getState().setGameListsData({
        type: GameListsType.Revision,
        gameLinkList: newGameLinkList,
        totalGames: newGameLinkList.length,
      });
      navigate(firstGame);
    } else {
      toast(t('No AI scheduled revision available yet.'), AppToastInfoOption);
    }
  }, [navigate, readyRevisionGames, t]);

  const onClickPracticeDetail = useCallback(() => {
    showPopup(
      <AboutDailyPracticePopup
        webBoosterType={userAccount?.settings.webBoosterType}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, showPopup, userAccount?.settings.webBoosterType]);

  const onClickBreadcrumb = useCallback(
    (folder: string) => {
      if (classroom) {
        showPopup(
          <ClubSelectFolderPopup
            webBoosterType={userAccount?.settings.webBoosterType}
            space={classroom}
            selectedFolderId={folder}
            folders={classroom.getAllPartitions(
              userAccount?.settings.webBoosterType,
            )}
            newFolder={undefined}
            onClickSelect={onClickBreadcrumb}
            onCancel={hidePopup}
            onConfirm={() => {
              useSessionStore.getState().setPartitionId(folder);
              hidePopup();
            }}
          />,
        );
      }
    },
    [userAccount?.settings.webBoosterType, classroom, hidePopup, showPopup],
  );

  const onClickPeriod = useCallback(() => {
    showPopup(
      <SelectPracticePeriodPopup
        selectPeriod={state.selectedPeriod}
        onPrimary={hidePopup}
        onPeriod={(selected, from, to) => {
          setState((s) => ({
            ...s,
            selectedPeriod: selected,
            from: from,
            to: to,
          }));
          hidePopup();
        }}
      />,
    );
  }, [hidePopup, showPopup, state.selectedPeriod]);

  const onClickPerformanceReport = useCallback(() => {
    navigate(`/club/${userId}/performance-report`);
  }, [navigate, userId]);

  // MARK: Fetch Performance Report
  const fetchSpacePerformanceReport = useCallback(() => {
    getPerformanceReport({
      classroomId: classroomId ?? '',
      webBoosterType: state.selectedBoosterType,
      partitionId:
        state.partitionId ===
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
          ? undefined
          : state.partitionId,
      from: todayPeriod?.from?.getTime().toString(),
      to: todayPeriod?.to?.getTime().toString(),
    })
      .then((summary) => {
        if (summary) {
          setState((s) => ({
            ...s,
            performanceData: summary.performanceReport,
          }));
        }
      })
      .catch(console.error);
  }, [
    classroomId,
    state.partitionId,
    state.selectedBoosterType,
    todayPeriod?.from,
    todayPeriod?.to,
  ]);

  useEffect(() => {
    if (classroomId) {
      fetchSpacePerformanceReport();
    }
  }, [classroomId, fetchSpacePerformanceReport]);

  const { avgGameScore, completedGames } = useMemo(() => {
    if (state.performanceData) {
      const completedGameCount = state.performanceData.completedGameCount;
      const scoreTotal = state.performanceData.scoreTotal;
      return {
        avgGameScore: parseFloat(
          numbro(
            completedGameCount > 0 ? scoreTotal / completedGameCount : 0,
          ).format({
            mantissa: 2,
            trimMantissa: true,
            optionalMantissa: true,
          }),
        ),
        completedGames: completedGameCount,
      };
    }
    return {
      avgGameScore: 0,
      completedGames: 0,
    };
  }, [state.performanceData]);

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      {/* <JOYAJOYAIMemoryBoostTemplate
        overallMemoryGraphData={overallMemoryGraphData}
        currentMemoryRetention={
          overallSummary?.forgettingCurve?.memoryRetentionValues[60] ?? 0
        }
        readyStatusItem={readyStatusItem}
        scheduledStatusItem={scheduledStatusItem}
        completedStatusItem={completedStatusItem}
        onClickInfo={onClickInfo}
        onClickMemoryInfo={onClickMemoryInfo}
        onClickBoostGame={() => {
          onClickBoostGame().catch(console.error);
        }}
        onClickAll={onClickAll}
        isDisableBoostGame={isDisableBoostGame}
      /> */}
      <AIMemoryBoostVer2Template
        selectedPeriod={
          getPracticePeriod(currentDate, state.selectedPeriod)?.name ?? ''
        }
        webBoosterType={state.selectedBoosterType}
        breadcrumbs={breadcrumbs}
        needToReviseAgainItems={
          needToReviseAgainItems.length > 0 ? needToReviseAgainItems : undefined
        }
        needToReviseNowItems={
          needToReviseNowItems.length > 0 ? needToReviseNowItems : undefined
        }
        toBeRevisedLaterItems={
          toBeRevisedLaterItems.length > 0 ? toBeRevisedLaterItems : undefined
        }
        recentlyRevisedItems={
          recentlyRevisedItems.length > 0 ? recentlyRevisedItems : undefined
        }
        onClickPeriod={onClickPeriod}
        onClickBreadcrumb={() => {
          onClickBreadcrumb(state.partitionId);
        }}
        onClickStartAIRevision={() => {
          onClickBoostGame();
        }}
        onClickPracticeDetail={onClickPracticeDetail}
        onClickPerformanceReport={onClickPerformanceReport}
        completedGameCount={completedGames}
        avgScore={avgGameScore}
      />
    </>
  );
};

export default AIMemoryBoostScreen;
